import axios from "axios";
import { SystemZone } from "luxon";
import posthog from "posthog-js";

import { BaseApiDatasource } from "@/io/datasource/BaseApiDatasource.ts";

class ApiDatasource extends BaseApiDatasource {
  unauthorizedInterceptor: number | undefined;
  refreshPromise: Promise<void> | undefined;

  createInstance = (token?: string) => {
    this.axiosInstance = axios.create({
      baseURL: import.meta.env.VITE_APP_API_URL || "/api/v1",
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "Content-Type": "application/json",
        Version: 2, // this is an API-specific value
        Timezone: SystemZone.instance.name,
      },
    });
    this.axiosInstance.interceptors.request.use(config => {
      const sessionId = posthog.get_session_id();
      if (sessionId) {
        config.headers["x-posthog-session-id"] = sessionId;
      }
      return config;
    });
  };

  /**
   * On a 401, refresh the session and then let the request fail.
   * The datasource never retries: retrying is the query layer's business.
   * The refresh is single-flighted because the api rotates and blacklists the
   * refresh token, so two concurrent refreshes would invalidate each other.
   */
  onUnauthorized = (refresh: () => Promise<void>) => {
    const instance = this.axiosInstance;
    if (!instance) {
      return;
    }
    this.ejectUnauthorizedInterceptor();

    this.unauthorizedInterceptor = instance.interceptors.response.use(undefined, async error => {
      if (error.response?.status === 401) {
        this.refreshPromise ??= refresh().finally(() => {
          this.refreshPromise = undefined;
        });
        // A failed refresh logs the user out (see useAuthenticate), nothing to handle here.
        await this.refreshPromise.catch(() => undefined);
      }

      return Promise.reject(error);
    });
  };

  destroyInstance = () => {
    if (this.axiosInstance) {
      this.ejectUnauthorizedInterceptor();
      this.refreshPromise = undefined;
      this.axiosInstance = undefined;
    }
  };

  private ejectUnauthorizedInterceptor = () => {
    if (this.axiosInstance && this.unauthorizedInterceptor !== undefined) {
      this.axiosInstance.interceptors.response.eject(this.unauthorizedInterceptor);
    }
    this.unauthorizedInterceptor = undefined;
  };
}

export const apiDataSource = new ApiDatasource();

/**
 * Unauthenticated sibling for the login endpoints (request/verify a code, refresh,
 * logout): no Bearer header, and no 401 handling — a 401 there means a bad code,
 * not a stale session.
 */
export const publicApiDataSource = new ApiDatasource();
publicApiDataSource.createInstance();
